import {
  caseworkNew,
  chaseFromLine,
  vanityFromLine,
  walkinShowerFromLine,
  waterClosetFromLine2,
} from "building-generation/elements/caseworks/casework";
import { caseworkLocalToWorldTransformFromHostPath } from "building-generation/elements/caseworks/caseworkLocalTransform";
import { getFloorElevationAndCeilingHeight } from "building-generation/generationTools/generateCeilingsTools";
import type { Casework } from "building-types/casework/caseworkTypes";
import { CaseworkTypeName } from "building-types/casework/caseworkTypesEnum";
import { PropInstanceHorizontalAlignment } from "building-types/datums/datumsTypesEnum";
import type { Doc } from "building-types/doc";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import { LoadConnectionType } from "building-types/electrical/electricalDeviceTypesEnum";
import type { LegacyRoomLayoutElectrical } from "building-types/electrical/electricalLegacyRoomLayoutTypes";
import {
  LegacyElectricalPositionType,
  LegacyPropPositionType,
} from "building-types/electrical/electricalLegacyRoomLayoutTypesEnum";
import { ControllerTypeSlot, LoadTypeSlot } from "building-types/electrical/electricalSlots";
import { ControllerSelectionType } from "building-types/electrical/electricalTypesEnum";
import type { Prop } from "building-types/props/propTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallStrip } from "building-types/walls/wallStrip";
import { COUNTER_HEIGHT, WALL_THICKNESS_INTERIOR } from "common/constants/buildingObjects";
import { degreeToRadian } from "common/utils/math";
import { BuiltInInteriorPropId } from "doc/elements/propCatalog/interiorPropCatalog";
import { defaultPropLabels } from "doc/propTools";
import { propOffsetFromLine2 } from "doc/rooms/propPlacement";
import { DIST_EPSILON } from "geom/core";
import { extrudePolyLine2InZ } from "geom/extrude";
import { offsetPolyLine2, polyLine2ThickenedEdge } from "geom/offset";
import {
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeLength,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Length,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { lineworkDoorSingle } from "../../elements/doors/doorOperationInfo";
import { wallWithDoorAndOppositeIndices } from "../walls/wall";
import { WallStripImpl } from "../walls/wallStripImpl";
import type { RoomLayoutFunc, RoomLayoutGenerator } from "./roomLayout";
import { overloadIndex } from "./roomLayout";

const bathElectric: LegacyRoomLayoutElectrical = {
  embeddedGroups: [
    {
      clearExistingLoadsInRoom: false,
      loads: [
        {
          type: "EMBEDDED",
          loads: [
            {
              loadTypeSlot: LoadTypeSlot.MOUNTED_WALL_LIGHT,
              queryPoint: [
                {
                  type: LegacyElectricalPositionType.PROP,
                  propId: BuiltInInteriorPropId.SINKVANITY,
                  propPosition: {
                    type: LegacyPropPositionType.OFFSET_FROM_PROP_BACK,
                    adjustmentNormal: 0,
                    adjustmentTangent: 0,
                  },
                },
              ],
            },
          ],
        },
      ],
      controllers: [
        {
          controllerTypeSlot: ControllerTypeSlot.PRIMARY,
          selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
          connection: {
            type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
            loadIndex: 0,
          },
        },
      ],
    },
    {
      clearExistingLoadsInRoom: false,
      loads: [
        {
          type: "EMBEDDED",
          loads: [
            {
              loadTypeSlot: LoadTypeSlot.OUTLET_GFCI,
              queryPoint: [
                {
                  type: LegacyElectricalPositionType.PROP,
                  propId: BuiltInInteriorPropId.TOWELHAND,
                  propPosition: {
                    type: LegacyPropPositionType.OFFSET_FROM_PROP_BACK,
                    adjustmentNormal: 0,
                    adjustmentTangent: 0,
                  },
                },
              ],
            },
          ],
        },
      ],
      controllers: [],
    },
    {
      clearExistingLoadsInRoom: false,
      loads: [
        {
          type: "EMBEDDED",
          loads: [
            {
              loadTypeSlot: LoadTypeSlot.BATH_FAN,
              queryPoint: [
                {
                  type: LegacyElectricalPositionType.PROP,
                  propId: BuiltInInteriorPropId.TOILET,
                  propPosition: {
                    type: LegacyPropPositionType.ROOM_WIDTH_PARAM_AT_PROP_AXIS,
                    param: 0.45,
                    paramNormalized: true,
                    reverse: false,
                    adjustmentNormal: 10,
                  },
                },
              ],
            },
          ],
        },
      ],
      controllers: [
        {
          controllerTypeSlot: ControllerTypeSlot.PRIMARY,
          selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
          connection: {
            type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
            loadIndex: 0,
          },
        },
      ],
    },
    {
      clearExistingLoadsInRoom: false,
      loads: [
        {
          type: "EMBEDDED",
          loads: [
            {
              loadTypeSlot: LoadTypeSlot.PRIMARY_LIGHT,
              queryPoint: [
                {
                  type: LegacyElectricalPositionType.PROP,
                  propId: BuiltInInteriorPropId.TOILET,
                  propPosition: {
                    type: LegacyPropPositionType.ROOM_WIDTH_PARAM_AT_PROP_AXIS,
                    param: 0.5,
                    paramNormalized: true,
                    reverse: true,
                    adjustmentNormal: -30,
                  },
                },
              ],
            },
          ],
        },
      ],
      controllers: [
        {
          controllerTypeSlot: ControllerTypeSlot.PRIMARY,
          selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
          connection: {
            type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
            loadIndex: 0,
          },
        },
      ],
    },
  ],
};

const bathFullLayout: RoomLayoutFunc = async ({ room, palette, wallIndex }) => {
  let vanityIndex;

  let doorStrip: WallStrip | null = null;
  let doorIndex = 0;
  let bathIndex = 2;
  let doorNextIndex;
  let flip = false;

  const strips = wallIndex.wallStripsFromRectRoom(room);

  for (let i = 0; i < strips.length; i++) {
    if (!strips[i]) {
      continue;
    }
    const doors = strips[i]?.doors();
    if (!!doors?.length) {
      doorStrip = strips[i];
      doorIndex = i;
      doorNextIndex = (doorIndex + 4 + 1) % 4;
    }
  }

  if (!doorStrip) {
    return null;
  }

  if (doorStrip.count > 1) {
    flip = true;
  }

  const doorBaseline = doorStrip.baseline(true);
  const doorNextBaseline = strips[doorNextIndex]?.baseline(true);

  let doorWallLong = false;
  if (doorNextBaseline && polyLine2EdgeLength(doorBaseline, 0) > polyLine2EdgeLength(doorNextBaseline, 0)) {
    doorWallLong = true;
  }

  let toiletPos;
  let sinkPos;
  let vanityStart;
  let vanityEnd;
  let vanityLen;
  let vanityBaseline;
  let bathBaseline;

  const bathShowerType = "Bath/shower combo";

  if (doorWallLong) {
    bathIndex = (doorIndex + 4 - 1) % 4;
    vanityIndex = (doorIndex + 4 + 2) % 4;
    bathBaseline = strips[bathIndex]?.baseline(true);
    vanityBaseline = strips[vanityIndex]?.baseline(true);
    vanityLen = vanityBaseline ? polyLine2EdgeLength(vanityBaseline, 0) : 0;
    sinkPos = (vanityLen - 58) / 2;
    toiletPos = vanityLen - 47 + 2.5;
    vanityStart = 0;
    vanityEnd = 58;
  } else {
    bathIndex = (doorIndex + 4 + 2) % 4;
    vanityIndex = (doorIndex + 4 - 1) % 4;
    bathBaseline = strips[bathIndex]?.baseline(true);
    if (flip) {
      vanityBaseline = strips[overloadIndex(vanityIndex + 2)]?.baseline(true);
      vanityLen = vanityBaseline ? polyLine2EdgeLength(vanityBaseline, 0) : 0;
      sinkPos = (vanityLen - 58) / 2;
      toiletPos = vanityLen - (47 - 2.5);
      vanityStart = 0;
      vanityEnd = 58;
    } else {
      vanityBaseline = strips[vanityIndex]?.baseline(true);
      vanityLen = vanityBaseline ? polyLine2EdgeLength(vanityBaseline, 0) : 0;
      sinkPos = vanityLen - (vanityLen - 58) / 2;
      toiletPos = 47 - 2.5;
      vanityStart = 58;
      vanityEnd = 0;
    }
  }

  const props: Prop[] = [];
  const casework: Casework[] = [];

  let propId: string;

  const bathLen = polyLine2EdgeLength(bathBaseline, 0);

  if (bathShowerType === "Bath/shower combo") {
    propId = BuiltInInteriorPropId.BATHCOMBO;

    const bath = await propOffsetFromLine2({
      settings: palette,
      propId: propId,
      room,
      edge: bathBaseline,
      z: room.level.floorElevation,
      horizontalAlignment: PropInstanceHorizontalAlignment.LEFT,
      offset: 0,
      param: bathLen,
      paramNormalized: false,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });

    if (bathLen > 60 + DIST_EPSILON) {
      // TODO: Maybe we should have some minimum chase size instead of DIST_EPSILON here?
      const chase = chaseFromLine(room, bathBaseline, 0, 60, 30);
      casework.push(chase);
    }

    props.push(bath);
  } else if (bathShowerType === "Bathtub") {
    propId = BuiltInInteriorPropId.BATHTUB;

    const bath = await propOffsetFromLine2({
      settings: palette,
      propId: propId,
      room,
      edge: bathBaseline,
      z: room.level.floorElevation,
      horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
      offset: 0,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });

    props.push(bath);
  } else if (bathShowerType === "Walk-in shower") {
    const shower = walkinShowerFromLine(room, bathBaseline, 0, 0, 30);
    casework.push(shower);
  }

  if (vanityBaseline) {
    const toilet = await propOffsetFromLine2({
      settings: palette,
      propId: BuiltInInteriorPropId.TOILET,
      room,
      edge: vanityBaseline,
      z: room.level.floorElevation,
      param: toiletPos,
      paramNormalized: false,
      horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
      offset: 0,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });

    props.push(toilet);

    if (vanityLen > 96) {
      let sinkBoolean = false;
      if (room.type === RoomType.JJ_BATH) {
        sinkBoolean = true;
      }

      if (sinkBoolean) {
        sinkPos -= 16;

        const sink1 = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.SINKVANITY,
          room,
          edge: vanityBaseline,
          z: room.level.floorElevation + COUNTER_HEIGHT,
          param: sinkPos + 28,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          offset: 2,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(sink1);
      }
    }

    const cabs = vanityFromLine(room, vanityBaseline, vanityStart, vanityEnd, 0);
    casework.push(cabs);

    const sink = await propOffsetFromLine2({
      settings: palette,
      propId: BuiltInInteriorPropId.SINKVANITY,
      room,
      edge: vanityBaseline,
      z: room.level.floorElevation + COUNTER_HEIGHT,
      param: sinkPos,
      paramNormalized: false,
      horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
      offset: 2,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });

    props.push(sink);
  }

  const conditionalParams: Doc["optionSets"] = {};

  return { props, casework, conditionalParams, electricLayouts: bathElectric };
};

const roomLayoutBathroom: RoomLayoutGenerator[] = [
  {
    roomType: RoomType.BATHFULL,
    generate: bathFullLayout,
  },
  {
    roomType: RoomType.BATHFULL_1_DOOR,
    generate: bathFullLayout,
  },
  {
    roomType: RoomType.MASTER_VANITY,
    async generate({ room, palette, wallIndex }) {
      const solidWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) => wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0,
        true,
      );

      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);

      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const vanityBaseline = solidWallLines[0];

      const sinkPos = polyLine2EdgeLength(vanityBaseline, 0) / 2;

      const props: Prop[] = [];

      const sink = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.SINKVANITY,
        room,
        edge: vanityBaseline,
        z: room.level.floorElevation + COUNTER_HEIGHT,
        param: sinkPos - 16,
        paramNormalized: false,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        offset: 2,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });

      const sink1 = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.SINKVANITY,
        room,
        edge: vanityBaseline,
        z: room.level.floorElevation + COUNTER_HEIGHT,
        param: sinkPos + 16,
        paramNormalized: false,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        offset: 2,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });

      props.push(sink, sink1);
      polyLine2ExtendEnd(vanityBaseline, -WALL_THICKNESS_INTERIOR / 2);
      polyLine2ExtendStart(vanityBaseline, -WALL_THICKNESS_INTERIOR / 2);

      const casework = [vanityFromLine(room, vanityBaseline, 0, 0, WALL_THICKNESS_INTERIOR / 2)];

      const electricLayouts: LegacyRoomLayoutElectrical = {
        embeddedGroups: [
          {
            clearExistingLoadsInRoom: false,
            loads: [
              {
                type: "EMBEDDED",
                loads: [
                  {
                    loadTypeSlot: LoadTypeSlot.OUTLET_GFCI,
                    queryPoint: [
                      {
                        type: LegacyElectricalPositionType.PROP,
                        propId: BuiltInInteriorPropId.TOWELHAND,
                        propPosition: {
                          type: LegacyPropPositionType.OFFSET_FROM_PROP_BACK,
                          adjustmentNormal: 0,
                          adjustmentTangent: 0,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
            controllers: [],
          },
          {
            clearExistingLoadsInRoom: false,
            loads: [
              {
                type: "EMBEDDED",
                loads: [
                  {
                    loadTypeSlot: LoadTypeSlot.PRIMARY_LIGHT,
                    queryPoint: [
                      {
                        type: LegacyElectricalPositionType.PROP,
                        propId: BuiltInInteriorPropId.SINKVANITY,
                        propPosition: {
                          type: LegacyPropPositionType.OFFSET_FROM_PROP_BACK,
                          adjustmentNormal: 12,
                          adjustmentTangent: -4,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
            controllers: [
              {
                controllerTypeSlot: ControllerTypeSlot.PRIMARY,
                selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
                connection: {
                  type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
                  loadIndex: 0,
                },
              },
            ],
          },
          {
            clearExistingLoadsInRoom: false,
            loads: [
              {
                type: "EMBEDDED",
                loads: [
                  {
                    loadTypeSlot: LoadTypeSlot.PRIMARY_LIGHT,
                    queryPoint: [
                      {
                        type: LegacyElectricalPositionType.REF_GEOM,
                        poly: offsetPolyLine2(polyLine2Edge(room.outline, 1), -25),
                        param: 0.3,
                        paramNormalized: true,
                        reverse: false,
                      },
                    ],
                  },
                ],
              },
            ],
            controllers: [
              {
                controllerTypeSlot: ControllerTypeSlot.PRIMARY,
                selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
                connection: {
                  type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
                  loadIndex: 0,
                },
              },
            ],
          },
          {
            clearExistingLoadsInRoom: false,
            loads: [
              {
                type: "EMBEDDED",
                loads: [
                  {
                    loadTypeSlot: LoadTypeSlot.PRIMARY_LIGHT,
                    queryPoint: [
                      {
                        type: LegacyElectricalPositionType.REF_GEOM,
                        poly: offsetPolyLine2(polyLine2Edge(room.outline, 1), -25),
                        param: 0.2,
                        paramNormalized: true,
                        reverse: false,
                      },
                    ],
                  },
                ],
              },
            ],
            controllers: [
              {
                controllerTypeSlot: ControllerTypeSlot.PRIMARY,
                selections: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
                connection: {
                  type: LoadConnectionType.EXPLICIT_LOAD_INDEX,
                  loadIndex: 0,
                },
              },
            ],
          },
        ],
      };

      return { props, casework, electricLayouts };
    },
  },
  {
    roomType: RoomType.BED_BATH,
    generate: bathFullLayout,
  },
  {
    roomType: RoomType.JJ_BATH,
    generate: bathFullLayout,
  },
  {
    roomType: RoomType.BATHTUB,
    async generate({ room, palette, wallIndex }) {
      const solidWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) => wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0,
        true,
      );

      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);

      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const bathBaseline = solidWallLines[0];

      const props: Prop[] = [];

      const bath = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.BATHTUB,
        room,
        edge: bathBaseline,
        z: room.level.floorElevation,
        param: 0.5,
        paramNormalized: true,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        offset: 2,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });

      props.push(bath);

      return { props };
    },
  },
  {
    roomType: RoomType.SHOWER,
    async generate({ room, wallIndex }): Promise<Partial<{
      props: Prop[];
      casework: Casework[];
      electricLayouts?: LegacyRoomLayoutElectrical | undefined;
    }> | null> {
      const solidWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) =>
          wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0 && wall.windowPlacements.length === 0,
        true,
      );

      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);
      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const windowWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) =>
          wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0 && wall.windowPlacements.length > 0,
        true,
      );

      const windowWallLines = WallStripImpl.allEndPoints(windowWallGroups);
      windowWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);
      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      let showerBaseline = solidWallLines[0];

      if (polyLine2EdgeLength(solidWallLines[1], 0) < 42) {
        showerBaseline = solidWallLines[1];
      }

      if (voidWallLines.length > 0) {
        showerBaseline = solidWallLines[0];
      }

      if (windowWallLines.length > 0) {
        showerBaseline = windowWallLines[0];
      }

      const showerDepth = polyLine2EdgeLength(solidWallLines[1], 0);
      const showerDoorLen = polyLine2EdgeLength(showerBaseline, 0);

      if (voidWallLines.length === 0 || polyLine2EdgeLength(voidWallLines[0], 0) < 42) {
        return null;
      }

      const showerGlassOffset = (showerDoorLen - 28) / 2;

      const casework: Casework[] = [];

      let showerBase = polyLine2ThickenedEdge(showerBaseline, 0, -showerDepth);
      showerBase = offsetPolyLine2(showerBase, WALL_THICKNESS_INTERIOR / 2);

      const showerFrame = polyLine2ThickenedEdge(showerBase, 2, -1.5);
      const doorEdge = polyLine2Edge(showerFrame, 2);

      polyLine2ExtendEnd(doorEdge, -showerGlassOffset);
      polyLine2ExtendStart(doorEdge, -showerGlassOffset);
      polyLine2Reverse(doorEdge);

      const { doorBodyLinework, doorSwingLinework } = lineworkDoorSingle(doorEdge, false, 45);

      const showerCross1 = polyLine2New([showerBase[0], showerBase[1], showerBase[4], showerBase[5]]);
      const showerCross2 = polyLine2New([showerBase[2], showerBase[3], showerBase[6], showerBase[7]]);

      const showerPan = extrudePolyLine2InZ(showerBase, room.level.floorElevation, 0.5);

      const planSolid = [
        showerFrame,
        showerBase,
        showerCross1,
        showerCross2,
        ...doorBodyLinework,
        ...doorSwingLinework,
      ];

      const showerCase = caseworkNew({
        room,
        planSolid,
        decorativeExtrusions: [showerPan],
        localToWorldTransform: caseworkLocalToWorldTransformFromHostPath(showerBaseline, [showerPan]),
        name: CaseworkTypeName.SHOWER,
      });
      casework.push(showerCase);

      return { casework };
    },
  },
  {
    roomType: RoomType.MASTER_BATH,
    async generate({ room, floor, palette, wallIndex }) {
      let vanityIndex;
      let doorStrip;
      let doorIndex;
      let bathIndex;
      let doorNextIndex;

      const strips = wallIndex.wallStripsFromRectRoom(room);

      let doors: GeneratedDoorPlacement[] | undefined = undefined;

      for (let i = 0; i < strips.length; i++) {
        if (!strips[i]) {
          continue;
        }
        doors = strips[i]?.doors();
        if (!!doors?.length) {
          doorStrip = strips[i];
          doorIndex = i;
          doorNextIndex = (doorIndex + 4 + 1) % 4;
        }
      }

      const doorBaseline = doorStrip.baseline(true);
      const doorNextBaseline = strips[doorNextIndex]?.baseline(true);
      const doorNextLen = doorNextBaseline ? polyLine2EdgeLength(doorNextBaseline, 0) : 0;
      const doorLen = polyLine2EdgeLength(doorBaseline, 0);

      let doorWallLong = false;
      if (polyLine2EdgeLength(doorBaseline, 0) > doorNextLen) {
        doorWallLong = true;
      }

      let toiletPos;
      let sinkPos;
      let vanityStart;
      let vanityEnd;
      let vanityLen;
      let vanityBaseline;
      let bathBaseline;
      let bathLen;
      let bathPos;
      let showerBaseline;
      let showerLen;
      let wcStart;
      let wcEnd;
      let toiletLine;
      const doorOppIndex = (doorIndex + 4 + 2) % 4;
      let doorOppLine;

      const waterCloset = false;

      if (doorWallLong) {
        vanityIndex = (doorIndex + 4 + 2) % 4;
        bathIndex = (vanityIndex + 4 + 1) % 4;
        const showerIndex = (vanityIndex + 4 - 1) % 4;
        bathBaseline = strips[bathIndex]?.baseline(true);
        vanityBaseline = strips[vanityIndex]?.baseline(true);
        showerBaseline = strips[showerIndex]?.baseline(true);
        showerLen = showerBaseline ? polyLine2EdgeLength(showerBaseline, 0) : 0;
        vanityLen = vanityBaseline ? polyLine2EdgeLength(vanityBaseline, 0) : 0;
        toiletLine = vanityBaseline;
        bathLen = bathBaseline ? polyLine2EdgeLength(bathBaseline, 0) : 0;
        sinkPos = vanityLen / 2;
        toiletPos = 18;
        vanityStart = 41;
        vanityEnd = 0;
        bathPos = bathLen - 30;
        wcStart = showerLen - 66;
        wcEnd = 0;
      } else {
        bathIndex = (doorIndex + 4 - 1) % 4;
        vanityIndex = (doorIndex + 4 + 1) % 4;
        doorOppLine = strips[doorOppIndex]?.baseline(true);
        bathBaseline = strips[bathIndex]?.baseline(true);
        vanityBaseline = strips[vanityIndex]?.baseline(true);
        showerBaseline = bathBaseline;
        vanityLen = vanityBaseline ? polyLine2EdgeLength(vanityBaseline, 0) : 0;
        bathLen = bathBaseline ? polyLine2EdgeLength(bathBaseline, 0) : 0;
        if (waterCloset) {
          vanityStart = 0;
          vanityEnd = 60;
          toiletLine = doorOppLine;
          toiletPos = 18;
          sinkPos = (vanityLen - 60) / 2;
        } else {
          vanityStart = 0;
          vanityEnd = 36;
          toiletLine = vanityBaseline;
          toiletPos = bathLen - 18;
          sinkPos = (vanityLen - 36) / 2;
        }
        bathPos = 30;
        showerLen = showerBaseline ? polyLine2EdgeLength(showerBaseline, 0) : 0;
        wcStart = showerLen - 60;
        wcEnd = 0;
      }

      const props: Prop[] = [];
      const casework: Casework[] = [];

      if (vanityBaseline) {
        casework.push(vanityFromLine(room, vanityBaseline, vanityStart, vanityEnd, 0));

        const sink = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.SINKVANITY,
          room,
          edge: vanityBaseline,
          z: room.level.floorElevation + COUNTER_HEIGHT,
          param: sinkPos - 18,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          offset: 2,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        const sink1 = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.SINKVANITY,
          room,
          edge: vanityBaseline,
          z: room.level.floorElevation + COUNTER_HEIGHT,
          param: sinkPos + 18,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          offset: 2,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(sink, sink1);
      }

      if (doorWallLong) {
        if (showerBaseline) {
          let showerWidth = Math.min(doorLen / 2 - 18, 36);
          showerWidth *= -1;
          const showerLine = polyLine2Copy(showerBaseline);
          polyLine2Reverse(showerLine);

          const showerDepth = 65;

          const shower = walkinShowerFromLine(room, showerLine, showerDepth, 0, showerWidth);
          const showerHead = await propOffsetFromLine2({
            settings: palette,
            propId: BuiltInInteriorPropId.SHOWER,
            room,
            edge: showerLine,
            z: room.level.floorElevation,
            param: showerDepth,
            offset: showerWidth / 2 + 2,
            rotate: degreeToRadian(-90),
            paramNormalized: false,
            horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
            catalogType: PropCatalogType.PROP_CATALOG,
            labels: defaultPropLabels(),
          });

          props.push(showerHead);
          casework.push(shower);
        }
      } else {
        if (bathBaseline) {
          showerLen = bathLen;
          const showerWidth = Math.min(doorLen / 2 - 18, 36);
          const shower = walkinShowerFromLine(room, bathBaseline, bathLen - 66, 0, showerWidth);
          const showerHead = await propOffsetFromLine2({
            settings: palette,
            propId: BuiltInInteriorPropId.SHOWER,
            room,
            edge: bathBaseline,
            z: room.level.floorElevation,
            param: bathLen - 66 + 5,
            offset: showerWidth / 2 + 2,
            rotate: degreeToRadian(-90),
            paramNormalized: false,
            horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
            catalogType: PropCatalogType.PROP_CATALOG,
            labels: defaultPropLabels(),
          });

          props.push(showerHead);
          casework.push(shower);
        }
      }

      const propToiletId: string = BuiltInInteriorPropId.TOILET;

      if (waterCloset) {
        const { floorElevation, ceilingHeight } = getFloorElevationAndCeilingHeight(room, floor);

        if (doorWallLong) {
          casework.push(
            waterClosetFromLine2(room, showerBaseline, floorElevation, ceilingHeight + 1, wcStart, wcEnd, 41),
          );
        } else {
          casework.push(
            waterClosetFromLine2(room, vanityBaseline, floorElevation, ceilingHeight + 1, wcStart, wcEnd, 41),
          );
        }
      }

      if (toiletLine) {
        const toilet = await propOffsetFromLine2({
          settings: palette,
          propId: propToiletId,
          room,
          edge: toiletLine,
          z: room.level.floorElevation,
          param: toiletPos,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(toilet);
      }

      const bathPropId = BuiltInInteriorPropId.BATHTUB;

      if (bathBaseline) {
        const bath = await propOffsetFromLine2({
          settings: palette,
          propId: bathPropId,
          room,
          edge: bathBaseline,
          z: room.level.floorElevation,
          param: bathPos,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(bath);
      }

      return { props, casework };
    },
  },
  {
    roomType: RoomType.POWDER,
    async generate({ room, palette, wallIndex }) {
      const rwalls = wallIndex.wallsInRoom(room);

      const [doorWallIndex, bathWallIndex] = wallWithDoorAndOppositeIndices(room, rwalls);
      if (doorWallIndex === -1 || bathWallIndex === -1) {
        return null;
      }

      let doorIndex;
      let plumbIndex;

      const strips = wallIndex.wallStripsFromRectRoom(room);

      let doors: GeneratedDoorPlacement[] | undefined = undefined;

      for (let i = 0; i < strips.length; i++) {
        if (!strips[i]) {
          continue;
        }
        doors = strips[i]?.doors();
        if (!!doors?.length) {
          doorIndex = i;
          plumbIndex = (doorIndex + 4 + 2) % 4;
        }
      }

      const plumbWallBaseline = strips[plumbIndex]?.baseline(true);
      const plumbLen = plumbWallBaseline ? polyLine2Length(plumbWallBaseline) : 0;

      const props: Prop[] = [];
      const casework: Casework[] = [];

      const propToiletId = BuiltInInteriorPropId.TOILET;

      if (plumbWallBaseline) {
        const toilet = await propOffsetFromLine2({
          settings: palette,
          propId: propToiletId,
          room,
          edge: plumbWallBaseline,
          z: room.level.floorElevation,
          param: 16,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(toilet);

        const sink = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.SINKPEDESTAL,
          room,
          edge: plumbWallBaseline,
          z: room.level.floorElevation,
          param: plumbLen - 30,
          paramNormalized: false,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(sink);
      }

      return { props, casework };
    },
  },
  {
    roomType: RoomType.WATER_CLOSET,
    async generate({ room, palette, wallIndex }) {
      const solidWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) =>
          wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0 && wall.windowPlacements.length === 0,
        true,
      );

      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);

      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));
      solidWallLines.reverse();

      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      let bathBaseline = solidWallLines[0];

      if (voidWallLines.length > 0) {
        bathBaseline = solidWallLines[0];
      }

      const props: Prop[] = [];

      const toilet = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.TOILET,
        room,
        edge: bathBaseline,
        z: room.level.floorElevation,
        param: 0.5,
        paramNormalized: true,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        offset: 2,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });

      props.push(toilet);

      return { props };
    },
  },
];

export default roomLayoutBathroom;
